import * as Sentry from '@sentry/browser';
import { ExtraErrorData } from '@sentry/integrations';

import { isProduction } from '@zapier/common-utils';

const ignoreErrors = [
  // Ignore http://bit.ly/2hk42Ap, resulting from what
  // appears to be a Chrome bug http://bit.ly/2gVbncS
  /__gCrWeb\.autofill\.extractForms/,
  // Ignore http://bit.ly/2hk71Zv, resulting from
  // some really strange issue in Safari 9. Experienced
  // by Mozilla team, too http://bit.ly/2hk9vHq
  /docs-homescreen-gb-container/,
  // Ignore http://bit.ly/2mVGcgX. It's a weird react error.
  // Google says it _might_ be a logging error? http://bit.ly/2lEB3ZW
  /e\.getHostNode/,
  // Ignore http://bit.ly/2lyZSao, resulting from
  // some Chrome plugin presumably.
  /ahk_main/,
  // Some weird firefox errors that seem to happen only on firefox for iOS.
  // https://bugzilla.mozilla.org/show_bug.cgi?id=1394296
  /__firefox__\./,
  // Ignore https://bit.ly/2U3OEM8 which looks like it stems
  // from https://bit.ly/2VdnUJi.
  /pktAnnotationHighlighter/,
  // Ignore https://sentry.io/organizations/zapier-1/issues/922193031
  // Which is a CodeMirror error that's really difficult to reproduce
  // yet occurs frequently, but it doesn't appear to impact users.
  /collapsed marker partially overlapping/,
  // Ignore a subset of NetworkError failures for the moment:
  // https://zapier.slack.com/archives/CAU27711A/p1566408226278600
  /having some trouble connecting/,
  // Users who are not authenticated can receive real warnings, but we
  // don't want to see them in Sentry.
  // https://sentry.io/organizations/zapier-1/issues/477245950/?project=55522
  /Not authenticated./,
];

const whitelistUrls = isProduction()
  ? [/zapier(-staging)?\.com/] // allow zapier-staging.com to also track errors
  : []; // remove whitelisting locally otherwise we'll never end on the `beforeSend` function below

const blacklistUrls = [
  /https:\/\/webcache.googleusercontent.com/, // Google's webcache of our pages (may include stale/outdated code which makes broken API calls)
];

export function sanitizeKeys(event) {
  const keys = ['password', 'secret', /token$/i, 'credentials'];

  for (const eventKey in event) {
    const removeKey = keys.reduce(
      (result, key) => result || eventKey.match(key) !== null,
      false
    );

    if (removeKey) {
      delete event[eventKey];
    } else if (typeof event[eventKey] === 'object') {
      sanitizeKeys(event[eventKey]);
    }
  }
}

const configureErrorTracking = (options = {}) => {
  Sentry.init({
    // Defaults to the DSN of the zapier-frontend project
    dsn:
      options.sentryDSN ||
      'https://739967869f5e4187ac584ed82319ea6d@app.getsentry.com/55522',

    // Associate error events with a release version, so we can track which release introduced new bugs.
    // More info on Sentry releases: https://docs.sentry.io/learn/releases/
    release: process.env.COMMIT_HASH || 'unknown-release',
    whitelistUrls,
    blacklistUrls,

    integrations: [
      // Disable global unhandled rejection tracking, to simulate the old behavior from a previous
      // version of Sentry we used.
      // We do this because we have a lot of unhandled rejection errors that we don't want to
      // track because they are expected, and track the ones we care about manually with the
      // `sentryErrorsMiddleware`.
      //
      // Note that doing this via the global handler with Sentry is a better solution, since
      // our GraphQL requests do not go through redux middlewares, so the middleware is no longer
      // a good abstraction to do this.
      // Disabling Sentry's unhandled rejection handler is a stop-gap until we properly migrate
      // the sentryErrorsMiddleware logic into a function that can be used in the `beforeSend`
      // callback below.
      new Sentry.Integrations.GlobalHandlers({
        onunhandledrejection: false,
      }),

      // Extracts all non-native attributes from the Error object and attaches them to the event as the `extra` data.
      // https://docs.sentry.io/platforms/javascript/#extraerrordata
      new ExtraErrorData(),
    ],

    beforeSend(event) {
      let ignore = false;
      if (event.exception) {
        event.exception.values.forEach(exception => {
          ignoreErrors.forEach(error => {
            ignore = ignore || exception.value.match(error) !== null;
          });
        });
      }
      if (ignore) {
        return null;
      }

      sanitizeKeys(event);

      if (!isProduction()) {
        console.log('Would have tracked to Sentry:', event); // eslint-disable-line no-console
        return null;
      }

      return event;
    },
  });
};

export default configureErrorTracking;
